import { useEffect, useRef } from "react";
import { Bold, Italic, Underline, List, ListOrdered, Link2, Heading2, Heading3, Type, RemoveFormatting } from "lucide-react";

// Lightweight rich-text editor (execCommand-based, no external deps).
// Emits sanitized-ready HTML via onChange.
export function RichTextEditor({ value = "", onChange, testid = "rich-text-editor", placeholder = "Write here…" }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only reset the DOM when the incoming value differs and the user isn't typing.
    if (document.activeElement !== el && el.innerHTML !== (value || "")) {
      el.innerHTML = value || "";
    }
  }, [value]);

  const emit = () => { if (ref.current && onChange) onChange(ref.current.innerHTML); };

  const exec = (cmd, arg = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const block = (tag) => exec("formatBlock", tag);

  const addLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    exec("createLink", href);
    // ensure new tab
    setTimeout(() => {
      ref.current?.querySelectorAll('a[href]').forEach((a) => { a.target = "_blank"; a.rel = "noopener noreferrer"; });
      emit();
    }, 0);
  };

  const Btn = ({ onClick, title, tid, children }) => (
    <button type="button" title={title} data-testid={tid} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className="h-8 min-w-8 px-2 inline-flex items-center justify-center rounded-md text-[#334155] hover:bg-[#E8E6E1] transition-colors">
      {children}
    </button>
  );

  return (
    <div className="rounded-xl border border-[#0F172A]/12 bg-white overflow-hidden" data-testid={`${testid}-wrap`}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#0F172A]/8 bg-[#F9F8F6] px-1.5 py-1">
        <Btn title="Bold" tid={`${testid}-bold`} onClick={() => exec("bold")}><Bold size={15} /></Btn>
        <Btn title="Italic" tid={`${testid}-italic`} onClick={() => exec("italic")}><Italic size={15} /></Btn>
        <Btn title="Underline" tid={`${testid}-underline`} onClick={() => exec("underline")}><Underline size={15} /></Btn>
        <span className="w-px h-5 bg-[#0F172A]/10 mx-1" />
        <Btn title="Normal text" tid={`${testid}-p`} onClick={() => block("<p>")}><Type size={15} /></Btn>
        <Btn title="Large heading" tid={`${testid}-h2`} onClick={() => block("<h2>")}><Heading2 size={16} /></Btn>
        <Btn title="Small heading" tid={`${testid}-h3`} onClick={() => block("<h3>")}><Heading3 size={15} /></Btn>
        <span className="w-px h-5 bg-[#0F172A]/10 mx-1" />
        <Btn title="Bullet list" tid={`${testid}-ul`} onClick={() => exec("insertUnorderedList")}><List size={15} /></Btn>
        <Btn title="Numbered list" tid={`${testid}-ol`} onClick={() => exec("insertOrderedList")}><ListOrdered size={15} /></Btn>
        <Btn title="Link" tid={`${testid}-link`} onClick={addLink}><Link2 size={15} /></Btn>
        <span className="w-px h-5 bg-[#0F172A]/10 mx-1" />
        <Btn title="Clear formatting" tid={`${testid}-clear`} onClick={() => exec("removeFormat")}><RemoveFormatting size={15} /></Btn>
      </div>
      <div
        ref={ref}
        data-testid={testid}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        data-placeholder={placeholder}
        className="rich-text rte-input min-h-[140px] px-4 py-3 text-sm text-[#0F172A] focus:outline-none"
      />
    </div>
  );
}
